import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import type { ValidProduct } from "@/hooks/use-product";
import { mapProductTypeToCategory } from "@/lib/category-mapping";
import { mapShopifyCategory } from "@/lib/shopify-transform";
import { orpc } from "@/utils/orpc";
import { product as productRoute, routes } from "@/utils/routes";

type RelatedProduct = {
  id: string;
  title: string;
  handle: string;
  price: string;
  image?: string;
  rating?: number | null;
  reviewCount?: number | null;
};

type RelatedProductsProps = {
  currentProduct: ValidProduct;
  excludeCurrentProduct?: boolean;
};

// Some backend responses may include rating/reviewCount; define a narrow type to read them safely
type ProductWithOptionalRatings = {
  rating?: number | null;
  reviewCount?: number | null;
};

/**
 * Format price from Shopify amount string to display format
 */
function formatPrice(amount: string): string {
  const price = Number.parseFloat(amount);
  return `£${price.toFixed(2)}`;
}

/**
 * Related products section showing recommended products by category
 * Fetches products from the same category as the current product
 */
export function RelatedProducts({
  currentProduct,
  excludeCurrentProduct = true,
}: RelatedProductsProps) {
  // Determine the category from the current product
  const productCategory = mapShopifyCategory(
    currentProduct.productType || "",
    currentProduct.tags || []
  );

  // Map display category to filter category (lowercase with hyphens)
  const getCategoryFilter = mapProductTypeToCategory;

  const categoryFilter = getCategoryFilter(productCategory);

  // Fetch related products by category
  const { data: relatedProductsData, isLoading } = useQuery({
    queryKey: ["related-products", categoryFilter, currentProduct.id],
    queryFn: async () => {
      const result = await orpc.shopify.getShopProducts.call({
        first: 8, // Fetch more than we need to account for filtering
        categories: [categoryFilter],
        sortKey: "BEST_SELLING",
      });
      return result;
    },
    enabled: !!categoryFilter,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Transform and filter products
  const relatedProducts: RelatedProduct[] = (
    relatedProductsData?.products || []
  )
    .filter((product) =>
      excludeCurrentProduct ? product.id !== currentProduct.id : true
    )
    .slice(0, 4) // Show max 4 products
    .map((product) => {
      const p = product as unknown as ProductWithOptionalRatings;
      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        price: formatPrice(product.priceRange.minVariantPrice.amount),
        image: product.images?.[0]?.url,
        rating: typeof p.rating === "number" ? p.rating : null,
        reviewCount: typeof p.reviewCount === "number" ? p.reviewCount : null,
      };
    });

  // Don't render if no related products found
  if (isLoading || relatedProducts.length === 0) {
    return null;
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-4">
        {/* Section Header */}
        <div className="mb-8 flex items-center justify-between">
          <h2 className="font-bold font-heading text-2xl text-foreground tracking-heading lg:text-3xl">
            You might also like
          </h2>
          <Button asChild variant="ghost">
            <Link
              className="text-primary hover:text-primary/80"
              href={routes.shop}
            >
              View all
            </Link>
          </Button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {relatedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Individual product card component
 */
function ProductCard({ product }: { product: RelatedProduct }) {
  return (
    <Link
      className="group block rounded-xl border bg-card p-4 transition-all hover:shadow-lg"
      href={{ pathname: productRoute(product.handle) }}
    >
      {/* Product Image */}
      <div className="relative mb-4 aspect-square overflow-hidden rounded-lg bg-muted">
        {product.image ? (
          <Image
            alt={product.title}
            className="object-cover transition-transform group-hover:scale-105"
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            src={product.image}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-muted">
            <span className="text-muted-foreground text-sm">No image</span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="space-y-2">
        {/* Rating */}
        {product.rating && (
          <div className="flex items-center gap-2">
            <StarRating rating={product.rating} />
            <span className="text-muted-foreground text-sm">
              ({product.reviewCount || 0})
            </span>
          </div>
        )}

        {/* Title */}
        <h3 className="line-clamp-2 font-medium text-foreground text-sm leading-tight transition-colors group-hover:text-primary">
          {product.title}
        </h3>

        {/* Price */}
        <p className="font-bold text-foreground">{product.price}</p>
      </div>
    </Link>
  );
}

/**
 * Star rating component
 */
function StarRating({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  // Create array of star states to avoid using array index as key
  const stars = Array.from({ length: 5 }, (_, index) => ({
    id: `star-${rating}-${index}`, // Stable key based on rating and index
    index,
    isFilled: index < fullStars,
    isHalf: index === fullStars && hasHalfStar,
  }));

  return (
    <div className="flex items-center gap-0.5">
      {stars.map((star) => {
        let stateLabel: string;
        if (star.isFilled) {
          stateLabel = "filled";
        } else if (star.isHalf) {
          stateLabel = "half";
        } else {
          stateLabel = "empty";
        }

        const label = `Star ${star.index + 1} — ${stateLabel}`;

        return (
          <span
            aria-label={label}
            className="inline-flex"
            key={star.id}
            role="img"
            title={label}
          >
            <Star
              aria-hidden="true"
              className={`h-4 w-4 ${
                star.isFilled || star.isHalf
                  ? "fill-primary text-primary"
                  : "fill-muted text-muted"
              }`}
            />
          </span>
        );
      })}
    </div>
  );
}
